import { createApi } from '@reduxjs/toolkit/query/react';
import { AxiosProgressEvent } from 'axios';

import { apiSahExternalBudgets } from '~constants/api';

import { rtkBaseQueryWrapper, unwrap } from '~libs/axiosWrapper';

interface AcoUploadResponse {
  message?: string;
}

export interface DuplicateExisting {
  id: string;
  sah_budget_id: string;
  total_amount: number | null;
  available_amount: number | null;
  used_amount: number | null;
}

export interface DuplicateIncoming {
  company_id: number;
  client_id: number;
  sah_budget_id: string;
  funding_source_id: number;
  start_date: string;
  end_date: string;
  total_amount: number | null;
  available_amount: number | null;
  used_amount: number | null;
  origin_tag: string;
  is_archived: boolean;
  is_deleted: boolean;
  meta_information: Record<string, unknown>;
}

export interface DuplicateGroup {
  client_id: number;
  client_name: string;
  funding_type: string;
  start_date: string;
  end_date: string;
  incoming: DuplicateIncoming;
  existing: DuplicateExisting[];
}

export interface AcoDetectResponse {
  duplicates: DuplicateGroup[];
  clean: number;
  clean_rows: DuplicateIncoming[];
  errors: Array<{ line: number; reason: string }>;
}

export interface AcoResolveRequest {
  merges: Array<{
    incoming_row: DuplicateIncoming;
    loser_ids: string[];
  }>;
  clean_rows: DuplicateIncoming[];
}

export interface AcoResolveResponse {
  created: number;
  updated: number;
  merged: number;
}

export const acoBudgetsApi = createApi({
  reducerPath: 'acoBudgetsApi',
  baseQuery: rtkBaseQueryWrapper({ baseUrl: apiSahExternalBudgets }),
  tagTypes: ['aco-budgets'],
  endpoints: (builder) => ({
    acoUpload: builder.mutation<AcoUploadResponse, { formData: FormData; onProgress?: (prog: number) => void }>({
      query: ({ formData, onProgress }) => ({
        url: '/aco-upload',
        method: 'POST',
        body: formData,
        onUploadProgress: (evt: AxiosProgressEvent) => {
          if (onProgress && evt.total) {
            const percent = Math.round((evt.loaded / evt.total) * 100);
            onProgress(percent);
          }
        },
      }),
      invalidatesTags: (_result, _error) => [{ type: 'aco-budgets', id: 'LIST' }],
      transformResponse: (payload) => unwrap<AcoUploadResponse>(payload),
    }),

    acoDetect: builder.mutation<AcoDetectResponse, { formData: FormData; onProgress?: (prog: number) => void }>({
      query: ({ formData, onProgress }) => ({
        url: '/aco-upload/detect',
        method: 'POST',
        body: formData,
        onUploadProgress: (evt: AxiosProgressEvent) => {
          if (onProgress && evt.total) {
            const percent = Math.round((evt.loaded / evt.total) * 100);
            onProgress(percent);
          }
        },
      }),
      transformResponse: (payload) => unwrap<AcoDetectResponse>(payload),
    }),

    acoResolve: builder.mutation<AcoResolveResponse, AcoResolveRequest>({
      query: (body) => ({
        url: '/aco-upload/resolve',
        method: 'POST',
        body,
      }),
      invalidatesTags: (_result, _error) => [{ type: 'aco-budgets', id: 'LIST' }],
      transformResponse: (payload) => unwrap<AcoResolveResponse>(payload),
    }),
  }),
});

export const { useAcoUploadMutation, useAcoDetectMutation, useAcoResolveMutation } = acoBudgetsApi;
